/**
 * BlobticDataManager - 100% Generic Client-Side Data Manager powered by Native IndexedDB
 * Fully self-hosted without external dependencies or domain-specific hardcoded data.
 * Reusable across all Blobtic applications (Academy, Parc, Maker, Planning, etc.).
 *
 * DB name is provided by the server as: {USR_UIDF}{DOS_UIDF}{appName}IdxDB
 * On F5/refresh the verify endpoint returns idxDBName — no localStorage needed.
 *
 * ─── Dynamic Per-Entity ObjectStore Architecture ───────────────────────────────
 * Each entity gets its own IDB objectStore (e.g. 'academy_room', 'academy_teacher').
 * ObjectStores are created LAZILY on first access — no upfront registration file needed.
 * When a new objectStore is needed, the DB version is incremented automatically.
 *
 * Usage pattern in domain stores:
 *   // In ensure*Loaded() — called once before reading:
 *   BlobticDataManager.registerEntity('academy_room', mapRoom,
 *     (items) => { this.rooms = items; this.notify(); });
 */

/** Single-item CRUD event emitted to registered entity handlers. */
export type CrudChangeEvent =
  | { action: 'upsert'; item: any }
  | { action: 'remove'; id: number | string };

/** @deprecated kept for call-site compatibility */
export interface ObjectStoreConfig {
  objectName: string;
  primaryKey?: string;
}

interface EntityRegistration {
  mapper: (dto: any) => any;
  setter: (items: any[]) => void;
  mappedCache: any[];
}

// ──────────────────────────────────────────────────────────────────────────────

class BlobticDataManagerService {
  private db: IDBDatabase | null = null;
  private dbName: string = '';

  // boubaker 22/08/2026 - interrupteur global IndexDB (paramètre IDXDB_CACHE_ENABLED).
  // false → isValidContext() = false → toutes les opérations (lecture, écriture,
  // suppression) sont neutralisées ; les données repartent toujours du serveur.
  private _enabled: boolean = true;

  /** Active/désactive globalement le cache IndexDB (paramètre système IDXDB_CACHE_ENABLED). */
  public setEnabled(enabled: boolean): void {
    this._enabled = enabled;
  }

  public isEnabled(): boolean {
    return this._enabled;
  }

  /** Raw DTO memory cache per entity. */
  private memoryCache: Map<string, any[]> = new Map();

  /** Entity registry: objectName → {mapper, setter, mappedCache}. */
  private entityRegistry: Map<string, EntityRegistration> = new Map();

  /** Global data change listeners for reactive store updates. */
  private changeListeners: ((objectName: string, action: 'upsert' | 'remove' | 'clear', data?: any) => void)[] = [];

  public subscribe(listener: (objectName: string, action: 'upsert' | 'remove' | 'clear', data?: any) => void): () => void {
    this.changeListeners.push(listener);
    return () => {
      this.changeListeners = this.changeListeners.filter(l => l !== listener);
    };
  }

  private _emitChange(objectName: string, action: 'upsert' | 'remove' | 'clear', data?: any): void {
    this.changeListeners.forEach(listener => {
      try {
        listener(objectName.toLowerCase(), action, data);
      } catch { /* silencieux */ }
    });
  }

  /**
   * Serialises all DB version upgrades — only one can run at a time.
   * Multiple simultaneous ensureObjectStore() calls queue on this promise
   * and their pending stores are batched into ONE version bump.
   */
  private _upgradePromise: Promise<IDBDatabase | null> | null = null;
  private _pendingStores: Set<string> = new Set();


  // ─── Entity registration ───────────────────────────────────────────────────

  /**
   * Registers a domain mapper + state setter for an entity type.
   * Call once (idempotent) from ensure*Loaded() before reading data.
   * After registration, upsertItem/removeItem automatically apply the mapper
   * and call setter with the updated domain array.
   *
   * @param objectName  e.g. 'academy_room'
   * @param mapper      raw DTO → domain object (e.g. mapRoom)
   * @param setter      e.g. (items) => { this.rooms = items; this.notify(); }
   */
  public registerEntity<T>(
    objectName: string,
    mapper: (dto: any) => T,
    setter: (items: T[]) => void
  ): void {
    const key = objectName.toLowerCase();
    if (this.entityRegistry.has(key)) return; // idempotent
    this.entityRegistry.set(key, { mapper, setter, mappedCache: [] });
  }

  /**
   * Seeds the entity's mapped cache from a domain array after a bulk load.
   * Call after registering AND after setting the store's in-memory array.
   */
  public seedEntityCache(objectName: string, domainItems: any[]): void {
    const reg = this.entityRegistry.get(objectName.toLowerCase());
    if (reg) reg.mappedCache = [...domainItems];
  }

  // ─── Context ───────────────────────────────────────────────────────────────

  public setContext(idxDBName: string): void {
    const name = String(idxDBName || '').trim();
    if (!name || name === 'undefined' || !name.endsWith('IdxDB')) {
      this.db?.close();
      this.db = null;
      this.dbName = '';
      this._enabled = false;
      this.memoryCache.clear();
      this.entityRegistry.clear();
      this._upgradePromise = null;
      this._pendingStores.clear();
      return;
    }
    if (this.dbName !== name) {
      this.db?.close();
      this.db = null;
      this.memoryCache.clear();
      this.entityRegistry.clear();
      this._upgradePromise = null;
      this._pendingStores.clear();
    }
    this.dbName = name;
    this._enabled = true;
  }

  public getDbName(): string { return this.dbName; }

  public isLoginOrPublicPage(): boolean {
    if (typeof window === 'undefined') return true;
    const path = window.location.pathname.toLowerCase();
    return (
      path.startsWith('/student-pre-registration') ||
      path.startsWith('/quiz') ||
      path.startsWith('/academy-quiz')
    );
  }

  public isValidContext(): boolean {
    if (!this._enabled) return false;
    return !!(this.dbName && this.dbName.endsWith('IdxDB'));
  }

  public clearSavedContext(): void {
    this.setContext('');
  }

  // ─── DB init ───────────────────────────────────────────────────────────────

  /**
   * Opens (or reuses) the IndexedDB connection.
   * Version starts at 1 and is bumped dynamically as new objectStores are needed.
   */
  public async initDB(idxDBName?: string): Promise<IDBDatabase | null> {
    if (idxDBName) this.setContext(idxDBName);
    if (!this.isValidContext()) return null;
    if (this.db) return this.db;

    return new Promise((resolve) => {
      // Open at whatever version IDB already has (or 1 for new DB)
      const req = indexedDB.open(this.dbName);

      req.onupgradeneeded = (_event: IDBVersionChangeEvent) => {
        // Initial open — objectStores are created on demand via ensureObjectStore()
      };


      req.onsuccess = (event: Event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve(this.db);
      };

      req.onerror = (event: Event) => {
        console.error(`BlobticDataManager: Failed to open IndexedDB (${this.dbName}):`,
          (event.target as IDBOpenDBRequest).error);
        resolve(null);
      };
    });
  }

  /**
   * Ensures objectStore exists for the given entity.
   *
   * Thread-safety: all concurrent calls queue on _upgradePromise so that
   * only ONE version bump happens at a time. All pending stores are batched
   * into that single upgrade — avoids 'connection is closing' errors when
   * many entities call setData/upsertItem simultaneously (e.g. saveToStorage).
   */
  private async ensureObjectStore(objectName: string): Promise<IDBDatabase | null> {
    const db = await this.initDB();
    if (!db) return null;

    // Store already exists — fast path
    if (db.objectStoreNames.contains(objectName)) return db;

    // Queue this store name for the next upgrade batch
    this._pendingStores.add(objectName);

    // If an upgrade is already running, wait for it — our store is included
    if (this._upgradePromise) {
      const upgraded = await this._upgradePromise;
      // If the store was created in that batch, we're done
      if (upgraded && upgraded.objectStoreNames.contains(objectName)) return upgraded;
      // Otherwise fall through to start another upgrade (edge case)
    }

    // Start a new serialised upgrade
    this._upgradePromise = this._runUpgrade();
    const result = await this._upgradePromise;
    this._upgradePromise = null;
    return result;
  }

  /** Performs one DB version bump, creating all pending + registered stores. */
  private _runUpgrade(): Promise<IDBDatabase | null> {
    const currentVersion = this.db?.version ?? 0;
    this.db?.close();
    this.db = null;

    // Collect every store that needs to be created in this batch
    const toCreate = new Set<string>(this._pendingStores);
    this._pendingStores.clear();
    this.entityRegistry.forEach((_, storeName) => toCreate.add(storeName));

    return new Promise<IDBDatabase | null>((resolve) => {
      const req = indexedDB.open(this.dbName, currentVersion + 1);

      req.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const upDb = (event.target as IDBOpenDBRequest).result;
        toCreate.forEach(storeName => {
          if (!upDb.objectStoreNames.contains(storeName)) {
            upDb.createObjectStore(storeName, { keyPath: 'id', autoIncrement: true });
          }
        });
      };

      req.onsuccess = (event: Event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve(this.db);
      };

      req.onerror = () => {
        console.error('BlobticDataManager: DB upgrade failed');
        resolve(null);
      };
    });
  }

  /**
   * boubaker 22/08/2026 - Connexion fiable pour écriture.
   * ensureObjectStore peut recréer la connexion (bump de version) : pendant ce
   * temps les autres écritures en cours détiennent une référence FERMÉE
   * ("The database connection is closing"). On relit donc toujours la connexion
   * courante (this.db) APRÈS l'attente.
   */
  private async _ensureWriteDb(objectName: string): Promise<IDBDatabase | null> {
    await this.ensureObjectStore(objectName);
    const db = this.db ?? (await this.initDB());
    if (!db || !db.objectStoreNames.contains(objectName)) return null;
    return db;
  }


  // ─── Entity ID resolution ──────────────────────────────────────────────────

  private static readonly OBJECT_PK_MAP: Record<string, string[]> = {
    'academy_discount_request': ['REQ_IDF', 'req_idf', 'REQ_UIDF', 'req_uidf'],
    'academy_room': ['ARM_IDF', 'arm_idf'],
    'academy_room_rental': ['RNT_IDF', 'rnt_idf'],
    'academy_teacher': ['TCH_IDF', 'tch_idf'],
    'academy_student': ['STD_IDF', 'std_idf'],
    'academy_course': ['CRS_IDF', 'crs_idf'],
    'academy_group': ['GRP_IDF', 'grp_idf'],
    'academy_group_student': ['GST_IDF', 'gst_idf'],
    'academy_session': ['SSN_IDF', 'ssn_idf'],
    'academy_attendance': ['ATD_IDF', 'atd_idf'],
    'academy_invoice': ['INV_IDF', 'inv_idf'],
    'academy_payment': ['PMT_IDF', 'pmt_idf'],
    'academy_quiz': ['QIZ_IDF', 'qiz_idf'],
    'academy_quiz_attempt': ['ATT_IDF', 'att_idf'],
    'academy_discount': ['DSC_IDF', 'dsc_idf'],
    'academy_planning_control': ['CTL_IDF', 'ctl_idf'],
    'sys_user': ['USR_IDF', 'usr_idf'],
    'sys_profil': ['PRF_IDF', 'prf_idf'],
    'sys_chat': ['MSG_IDF', 'CHT_IDF', 'msg_idf', 'cht_idf'],
    'chat_message': ['MSG_IDF', 'CHT_IDF', 'msg_idf', 'cht_idf'],
    'sys_notification': ['NOT_IDF', 'NOT_UIDF', 'not_idf', 'not_uidf'],
    'user_notification': ['NOT_IDF', 'NOT_UIDF', 'not_idf', 'not_uidf'],
    'parc_vehicle': ['VEH_IDF', 'veh_idf'],
    'parc_driver': ['DRV_IDF', 'drv_idf'],
    'parc_trip': ['TRP_IDF', 'trp_idf'],
    'parc_repair': ['RPR_IDF', 'rpr_idf'],
    'parc_fuel': ['FUL_IDF', 'ful_idf'],
    'parc_fuel_card': ['CRD_IDF', 'crd_idf'],
    'parc_maintenance': ['MNT_IDF', 'mnt_idf'],
    'parc_document': ['DOC_IDF', 'doc_idf'],
    'tabp_dossier': ['DOS_IDF', 'dos_idf'],
    'dossier': ['DOS_IDF', 'dos_idf'],
    'agency': ['DOS_IDF', 'dos_idf'],
  };

  public getEntityId(objectName: string, item: any): number | string | null {
    if (item == null) return null;
    if (typeof item === 'number' || typeof item === 'string') return item;
    if (typeof item === 'object') {
      if (item.id !== undefined && item.id !== null && item.id !== '') return item.id;
      if (item.ID !== undefined && item.ID !== null && item.ID !== '') return item.ID;

      const objKey = (objectName || '').toLowerCase();
      const knownPks = BlobticDataManagerService.OBJECT_PK_MAP[objKey];
      if (knownPks) {
        for (const pk of knownPks) {
          if (item[pk] !== undefined && item[pk] !== null && item[pk] !== '') {
            return item[pk];
          }
        }
      }

      const keys = Object.keys(item);
      for (const k of keys) {
        if ((k.endsWith('_IDF') || k.endsWith('_id') || k.endsWith('_ID')) && k !== 'DOS_IDF' && k !== 'USER_IDF' && k !== 'dos_idf' && k !== 'user_idf') {
          if (item[k] !== undefined && item[k] !== null && item[k] !== '') return item[k];
        }
      }

      for (const k of keys) {
        if (k.endsWith('_IDF') || k.endsWith('_id') || k.endsWith('_ID') || k.endsWith('_UIDF') || k.endsWith('_uidf')) {
          if (item[k] !== undefined && item[k] !== null && item[k] !== '') return item[k];
        }
      }
    }
    return null;
  }

  // ─── Memory helpers ────────────────────────────────────────────────────────

  public hasData(objectName: string): boolean {
    return (this.memoryCache.get(objectName.toLowerCase())?.length ?? 0) > 0;
  }

  public getMemoryData<T>(objectName: string): T[] | null {
    return (this.memoryCache.get(objectName.toLowerCase()) as T[]) ?? null;
  }

  // ─── Read ──────────────────────────────────────────────────────────────────

  /**
   * Returns all raw items for an entity (memory first, then IDB objectStore).
   * Returns null when not logged in; [] when logged in but no data stored yet.
   */
  public async getData<T>(objectName: string): Promise<T[] | null> {
    if (!this.isValidContext()) return null;
    const key = objectName.toLowerCase();

    // Memory hit
    if (this.memoryCache.has(key)) return this.memoryCache.get(key) as T[];

    try {
      // boubaker 22/08/2026 - 2 tentatives : une écriture/invalidation concurrente
      // peut fermer la connexion le temps d'un upgrade → reprise avec connexion fraîche.
      for (let attempt = 0; attempt < 2; attempt++) {
        const db = await this.initDB();
        if (!db || !db.objectStoreNames.contains(key)) return []; // store not yet created = empty

        try {
          return await new Promise<T[]>((resolve) => {
            const tx = db.transaction(key, 'readonly');
            const req = tx.objectStore(key).getAll();
            req.onsuccess = () => {
              const items = (req.result as T[]) || [];
              this.memoryCache.set(key, items);
              resolve(items);
            };
            req.onerror = () => resolve([]);
          });
        } catch (err: any) {
          // Connexion fermée par un upgrade concurrent → réessayer une fois
          if (attempt === 1 || err?.name !== 'InvalidStateError') throw err;
        }
      }
      return [];
    } catch (err) {
      console.error(`BlobticDataManager.getData error (${key}):`, err);
      return null;
    }
  }

  // ─── Write (bulk) ──────────────────────────────────────────────────────────

  /**
   * Replaces all stored items for an entity.
   * Creates the objectStore on-the-fly if it doesn't exist yet.
   * Does NOT fire notifyChange — caller manages domain state after setData.
   */
  public async setData<T>(objectName: string, items: T[]): Promise<boolean> {
    if (!this.isValidContext()) return false;
    const key = objectName.toLowerCase();
    const cleanItems = Array.isArray(items) ? items : [];
    this.memoryCache.set(key, cleanItems);

    try {
      // boubaker 22/08/2026 - 2 tentatives : si la connexion est fermée entre-temps
      // (upgrade concurrent déclenché par une autre écriture), on reprend une
      // connexion fraîche et on rejoue la transaction.
      for (let attempt = 0; attempt < 2; attempt++) {
        const db = await this._ensureWriteDb(key);
        if (!db) return false;

        try {
          return await new Promise<boolean>((resolve) => {
            const tx = db.transaction(key, 'readwrite');
            const store = tx.objectStore(key);
            store.clear();

            for (const item of cleanItems) {
              if (item && typeof item === 'object') {
                const id = this.getEntityId(key, item);
                if (id !== null && id !== undefined && id !== '') {
                  const itemToStore: any = { ...item, id };
                  try {
                    store.put(itemToStore);
                  } catch { /* ignorer les items invalides */ }
                }
              }
            }

            tx.oncomplete = () => resolve(true);
            tx.onerror = (err) => {
              console.error(`BlobticDataManager.setData failed (${key}):`, err);
              resolve(false);
            };
          });
        } catch (err: any) {
          // Connexion fermée par un upgrade concurrent → réessayer une fois
          if (attempt === 1 || err?.name !== 'InvalidStateError') throw err;
        }
      }
      return false;
    } catch (err) {
      console.error(`BlobticDataManager.setData error (${key}):`, err);
      return false;
    }
  }

  // ─── Write (single item) ───────────────────────────────────────────────────

  /**
   * Inserts or updates one item in memory + IndexedDB.
   * Creates the objectStore on-the-fly if it doesn't exist.
   * Fires entity handler (mapper → setter) immediately for instant UI update.
   */
  public async upsertItem<T extends Record<string, any>>(objectName: string, item: T): Promise<boolean> {
    if (!this.isValidContext() || !item) return false;
    const key = objectName.toLowerCase();
    const itemId = this.getEntityId(key, item);
    if (itemId === null || itemId === undefined || itemId === '') return false;

    // Update raw memory cache
    const current = this.memoryCache.get(key) || [];
    const ci = current.findIndex((x: any) => this.getEntityId(key, x) === itemId);
    this.memoryCache.set(key, ci >= 0
      ? [...current.slice(0, ci), { ...current[ci], ...item }, ...current.slice(ci + 1)]
      : [...current, item]
    );

    // Notify entity handler (applies mapper, calls setter)
    this._notifyRegistry(key, { action: 'upsert', item });

    // Emit global change event so domain stores can invalidate/refresh reactively
    this._emitChange(key, 'upsert', item);

    // Async IDB write
    try {
      // boubaker 22/08/2026 - connexion relue après ensureObjectStore (cf. _ensureWriteDb)
      const db = await this._ensureWriteDb(key);
      if (!db) return false;

      return new Promise<boolean>((resolve) => {
        try {
          const tx = db.transaction(key, 'readwrite');
          const itemToStore: any = { ...item, id: itemId };
          const req = tx.objectStore(key).put(itemToStore);
          req.onerror = () => resolve(false);
          tx.oncomplete = () => resolve(true);
          tx.onerror = () => resolve(false);
        } catch {
          resolve(false);
        }
      });
    } catch { return false; }
  }

  /**
   * Removes one item from memory + IndexedDB.
   * Fires entity handler immediately.
   */
  public async removeItem(objectName: string, target: number | string | any): Promise<boolean> {
    if (!this.isValidContext()) return false;
    const key = objectName.toLowerCase();
    const itemId = this.getEntityId(key, target);
    if (itemId == null) return false;

    // Update raw memory cache
    const current = this.memoryCache.get(key) || [];
    this.memoryCache.set(key, current.filter((x: any) => this.getEntityId(key, x) !== itemId));

    // Notify entity handler
    this._notifyRegistry(key, { action: 'remove', id: itemId });

    // Emit global change event so domain stores can invalidate/refresh reactively
    this._emitChange(key, 'remove', itemId);

    // Async IDB write
    try {
      const db = await this.initDB(); // store must already exist for a remove
      if (!db || !db.objectStoreNames.contains(key)) return false;

      return new Promise<boolean>((resolve) => {
        const tx = db.transaction(key, 'readwrite');
        tx.objectStore(key).delete(itemId);
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
      });
    } catch { return false; }
  }

  // ─── Internal: fire registered entity handler ──────────────────────────────

  private _notifyRegistry(key: string, event: CrudChangeEvent): void {
    const reg = this.entityRegistry.get(key);
    if (!reg) return;

    if (event.action === 'upsert') {
      const mapped = reg.mapper(event.item);
      const id = mapped?.id ?? this.getEntityId(key, event.item);
      const ci = reg.mappedCache.findIndex(
        (x: any) => x.id === id || String(x.id) === String(id)
      );
      reg.mappedCache = ci >= 0
        ? [...reg.mappedCache.slice(0, ci), { ...reg.mappedCache[ci], ...mapped }, ...reg.mappedCache.slice(ci + 1)]
        : [...reg.mappedCache, mapped];
    } else {
      reg.mappedCache = reg.mappedCache.filter(
        (x: any) => x.id !== event.id && String(x.id) !== String(event.id)
      );
    }

    reg.setter([...reg.mappedCache]);
  }

  // ─── Clear all ────────────────────────────────────────────────────────────

  public async clearAll(): Promise<boolean> {
    this.memoryCache.clear();
    this.entityRegistry.forEach(reg => { reg.mappedCache = []; });
    this._emitChange('*', 'clear');

    const db = await this.initDB();
    if (!db) return true;

    try {
      const storeNames = Array.from(db.objectStoreNames);
      if (storeNames.length === 0) return true;

      return new Promise<boolean>((resolve) => {
        const tx = db.transaction(storeNames, 'readwrite');
        storeNames.forEach(name => tx.objectStore(name).clear());
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
      });
    } catch { return false; }
  }

  /**
   * boubaker 22/08/2026 - Supprime TOUS les object stores (drop complet de la
   * base IndexDB, pas seulement le vidage du contenu) : ferme la connexion,
   * supprime la base puis réinitialise les caches mémoire. Les stores seront
   * recréés dynamiquement au prochain accès (ensureObjectStore / bump version).
   * Utilisé par le bouton « Initialiser » du Dashboard Academy.
   */
  public async dropAllStores(): Promise<boolean> {
    this.memoryCache.clear();
    this.entityRegistry.forEach(reg => { reg.mappedCache = []; });
    this._emitChange('*', 'clear');

    const dbName = this.dbName;
    // Fermer la connexion courante : deleteDatabase reste bloqué tant qu'une
    // connexion est ouverte sur la base.
    try { this.db?.close(); } catch { /* silencieux */ }
    this.db = null;
    this._upgradePromise = null;
    this._pendingStores.clear();

    if (!dbName || typeof indexedDB === 'undefined') return true;

    return new Promise<boolean>((resolve) => {
      let settled = false;
      const done = (ok: boolean) => { if (!settled) { settled = true; resolve(ok); } };
      const req = indexedDB.deleteDatabase(dbName);
      req.onsuccess = () => done(true);
      req.onerror = () => done(false);
      // Un autre onglet/fenêtre maintient la base ouverte : ne pas attendre
      req.onblocked = () => done(true);
    });
  }

  /**
   * boubaker 22/08/2026 - IndexDB multi-utilisateurs.
   * Vide le contenu (les objets) des object stores indiqués sans les supprimer
   * (pas de bump de version nécessaire). Utilisé quand le serveur signale des
   * stores invalidés (lignes fermées TABP_SYS_USER_IDX_DB).
   * Le registre des entités est réinitialisé : le prochain ensure*Loaded()
   * rechargera automatiquement depuis le serveur.
   */
  public async removeStores(objectNames: string[]): Promise<boolean> {
    if (!Array.isArray(objectNames) || objectNames.length === 0) return true;

    const keys = objectNames
      .filter(n => typeof n === 'string' && n.trim().length > 0)
      .map(n => n.trim().toLowerCase());

    // Mémoire : vider le cache brut et les caches mappés enregistrés
    const notified: string[] = [];
    keys.forEach(key => {
      this.memoryCache.delete(key);
      const reg = this.entityRegistry.get(key);
      if (reg) {
        reg.mappedCache = [];
        notified.push(key);
      }
      this._emitChange(key, 'clear');
    });
    // Notifier les entités enregistrées (setter([])) pour vider l'état du domaine
    notified.forEach(key => {
      const reg = this.entityRegistry.get(key);
      try { reg?.setter([]); } catch { /* silencieux */ }
    });

    // IndexedDB : vider les stores existants
    try {
      const db = await this.initDB();
      if (!db) return true;
      const existing = Array.from(db.objectStoreNames).filter(n => keys.includes(n.toLowerCase()));
      if (existing.length === 0) return true;

      return new Promise<boolean>((resolve) => {
        const tx = db.transaction(existing, 'readwrite');
        existing.forEach(name => tx.objectStore(name).clear());
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
      });
    } catch { return false; }
  }

  // ─── Backward-compatibility shims ─────────────────────────────────────────

  /** @deprecated No-op. Stores are now created dynamically on first access. */
  public registerStore(_objectName: string, _primaryKey?: string): void { }

  /** @deprecated No-op. Stores are now created dynamically on first access. */
  public registerStores(_config: Record<string, string>): void { }

  /** @deprecated Always returns 'id'. */
  public getPrimaryKeyName(_objectName: string): string { return 'id'; }
}

export const BlobticDataManager = new BlobticDataManagerService();
